import React, { useEffect, useState } from 'react';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Button from '@material-ui/core/Button';

import { httpClient } from '../utils/dataProvider'
import { PageHeader, StatusPill, MetricCard, Surface, DetailGrid, DetailItem, ActionButton } from '../components/ui';
import { tokens } from '../theme';

const PAYOUT_METHODS = ['bank', 'whish', 'crypto', 'stripe'];
const isWithdrawal = (tx) => tx.type === 'debit' && PAYOUT_METHODS.includes(tx.method);
const isDeposit = (tx) => tx.type === 'credit' && PAYOUT_METHODS.includes(tx.method);
const isBookingFeeRow = (tx) => tx.method === 'in-app' || tx.method === 'cash-payment-fee';

// minor units (cents) -> dollars string
const money = (minor) => (Number(minor || 0) / 100).toFixed(2);
const feeModeLabel = (mode) => (mode === 'brackets' ? 'Brackets' : mode === 'flat' ? 'Flat' : 'Unknown');

// Capitalise a payout method for display: "whish" -> "Whish", "bank" -> "Bank".
const capitalise = (s) => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : '—');

// Human label for the kind of money movement a transaction represents.
const kindLabel = (tx) => {
  if (isWithdrawal(tx)) return 'Withdrawal';
  if (isDeposit(tx)) return 'Deposit';
  if (tx.method === 'in-app') return 'Booking';
  if (tx.method === 'cash-payment-fee') return 'Cash fee';
  return capitalise(tx.type);
};

// The destination `tx.details` points at depends on the payout method.
const destinationLabel = (method) => {
  if (method === 'whish') return 'Whish phone';
  if (method === 'bank') return 'IBAN';
  if (method === 'crypto') return 'Wallet';
  if (method === 'stripe') return 'Stripe account';
  return 'Destination';
};

// External ref is optional; empty / NOT_SET placeholders render as a dash.
const externalRefDisplay = (tx) => {
  const ref = tx.id;
  if (!ref || ref === 'NOT_SET') return '—';
  return ref;
};

export default function Financials() {
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [tab, setTab] = useState('pending');

  const load = () => {
    setLoading(true);
    httpClient('https://skilzapp.net/api/users', {
      method: 'GET',
    })
      .then(({ json }) => {
        setUsers(json.docs);
        setTransactions(json.docs.flatMap(user => (user.transactions || []).map(tx => ({ ...tx, phone: user.phone, userName: user.name, userId: user._id })))
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  }
  useEffect(() => {
    load()
  }, []);

  // Actual platform fee for a booking transaction. New transactions carry an
  // exact feeSnapshot; older ones fall back to the stored rate (skilzFee) or 5%.
  const actualFee = (tx) => {
    if (tx.feeSnapshot && typeof tx.feeSnapshot.feeMinor === 'number') {
      return tx.feeSnapshot.feeMinor / 100;
    }
    return (tx.amount || 0) * (tx.skilzFee || 0.05);
  };

  // Net a withdrawal actually transfers to the provider. The breakdown's
  // netMinor is authoritative; legacy rows (no breakdown) transfer the gross.
  const netToTransfer = (tx) => {
    if (tx.withdrawalBreakdown && typeof tx.withdrawalBreakdown.netMinor === 'number') {
      return tx.withdrawalBreakdown.netMinor / 100;
    }
    return tx.amount || 0;
  };

  const confirmed = transactions.filter(tx => tx.status === 'confirmed');

  // Skilz revenue = the fee the platform actually keeps. Counted once per
  // booking off the client `debit` row (the provider `credit` row mirrors the
  // same feeSnapshot, so summing both would double-count). Withdrawal
  // commission is Skilz revenue; tax is withheld for the state, not revenue.
  const cashPaymentFees = confirmed
    .filter(tx => tx.type === 'debit' && tx.method === 'cash-payment-fee')
    .reduce((acc, tx) => acc + (tx.amount || 0), 0);
  const inAppFees = confirmed
    .filter(tx => tx.type === 'debit' && tx.method === 'in-app')
    .reduce((acc, tx) => acc + actualFee(tx), 0);
  const withdrawalCommission = confirmed
    .filter(isWithdrawal)
    .reduce((acc, tx) => acc + (tx.withdrawalBreakdown && typeof tx.withdrawalBreakdown.commissionMinor === 'number' ? tx.withdrawalBreakdown.commissionMinor / 100 : 0), 0);
  const skilzBalance = cashPaymentFees + inAppFees + withdrawalCommission;

  // Tax withheld (Lebanon 8.25%) is passed to the state, tracked separately.
  const taxWithheld = confirmed
    .filter(isWithdrawal)
    .reduce((acc, tx) => acc + (tx.withdrawalBreakdown && typeof tx.withdrawalBreakdown.taxMinor === 'number' ? tx.withdrawalBreakdown.taxMinor / 100 : 0), 0);

  // Live fee mode: no admin pricing endpoint carries it, so derive it from the
  // newest transaction that carries a snapshot (transactions are sorted newest
  // first). feeSnapshot.mode and withdrawalBreakdown.feeMode use the same enum.
  const feeMode = (() => {
    for (const tx of transactions) {
      if (tx.feeSnapshot && tx.feeSnapshot.mode) return tx.feeSnapshot.mode;
      if (tx.withdrawalBreakdown && tx.withdrawalBreakdown.feeMode) return tx.withdrawalBreakdown.feeMode;
    }
    return null;
  })();

  // Compact "rate% · tier · $min–$max" descriptor for a fee/withdrawal snapshot.
  const bracketDescriptor = ({ rateBps, tier, bracketMin, bracketMax }) => {
    const parts = [];
    if (typeof rateBps === 'number') parts.push(`${(rateBps / 100).toFixed(2)}%`);
    if (tier) parts.push(tier);
    if (typeof bracketMin === 'number' && typeof bracketMax === 'number') {
      parts.push(`$${money(bracketMin)}–$${money(bracketMax)}`);
    }
    return parts.join(' · ');
  };

  // Brackets-mode withdrawal breakdown rows, rendered as DetailItems inside the
  // card's grid. In flat mode commission/tax are 0 by design, so those lines are
  // omitted entirely (a dim note explains why); legacy rows have no breakdown.
  const withdrawalBreakdownItems = (tx) => {
    const wb = tx.withdrawalBreakdown;
    if (!wb || wb.feeMode === 'flat') return null;
    const descriptor = bracketDescriptor({ rateBps: wb.commissionRateBps, tier: wb.tier, bracketMin: wb.bracketMin, bracketMax: wb.bracketMax });
    const taxValue = wb.taxApplied
      ? `−$${money(wb.taxMinor)} (${((wb.taxBps || 0) / 100).toFixed(2)}%)`
      : 'none — KYC L2 verified';
    return (
      <>
        <DetailItem label="Commission" value={`−$${money(wb.commissionMinor)}${descriptor ? ` (${descriptor})` : ''}`} />
        <DetailItem label="Invoiced" value={`$${money(wb.invoicedMinor)} ${tx.currency}`} />
        <DetailItem label="Tax" value={taxValue} />
      </>
    );
  };

  // The highlighted "Net to transfer" line — the amount the admin actually pays —
  // plus the dim note for flat/legacy rows. Rendered below the grid.
  const withdrawalNetLine = (tx) => {
    const wb = tx.withdrawalBreakdown;
    const note = !wb
      ? 'legacy — no breakdown'
      : wb.feeMode === 'flat'
        ? 'Flat mode — no commission/tax applied'
        : null;
    return (
      <div style={{ marginTop: 14 }}>
        {note && <div style={{ fontSize: 12, color: tokens.muted, marginBottom: 8 }}>{note}</div>}
        <div style={{
          display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12,
          padding: '12px 16px', borderRadius: 12,
          background: `${tokens.success}14`, border: `1px solid ${tokens.success}33`,
        }}>
          <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.065em', textTransform: 'uppercase', color: tokens.success }}>
            Net to transfer
          </span>
          <strong style={{ fontSize: 20, fontWeight: 800, color: tokens.success }}>
            ${netToTransfer(tx).toFixed(2)} {tx.currency}
          </strong>
        </div>
      </div>
    );
  };

  return (
    <main className="sk-page sk-financials" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <PageHeader eyebrow="Finance" title="Financials" description="Monitor platform balances, payouts, and transaction status." />
      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'stretch', gap: 10 }}>
        <Card className="sk-card--green" style={{ flex: 1 }}>
          <CardHeader title="Skilz Balance" />
          <CardContent><h2>$ {parseFloat(skilzBalance).toFixed(2)}</h2></CardContent>
        </Card>
        <Card className="sk-card--blue" style={{ flex: 1 }}>
          <CardHeader title="Users Balance" />
          <CardContent>
            <h2>$ {parseFloat(transactions.filter(tx => tx.status === 'confirmed').reduce((acc, transaction) => transaction.type === 'credit' ? acc + transaction.amount : acc - transaction.amount, 0)).toFixed(2)}</h2>
          </CardContent>
        </Card>
        <Card className="sk-card--purple" style={{ flex: 1 }}>
          <CardHeader title="Payouts" />
          <CardContent><h2>$ {parseFloat(transactions.filter(tx => tx.type === 'debit' && tx.status === 'confirmed' && (tx.method === 'bank' || tx.method === 'whish' || tx.method === 'crypto')).reduce((acc, transaction) => acc + transaction.amount, 0)).toFixed(2)}</h2></CardContent>
        </Card>
      </div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <MetricCard tone="orange" label="Tax withheld (8.25%)" value={`$${taxWithheld.toFixed(2)}`} detail="Lebanon withholding on confirmed withdrawals" />
        <MetricCard tone="blue" label="Fee mode" value={feeModeLabel(feeMode)} detail={feeMode ? 'Derived from latest transaction snapshot' : 'No snapshotted transactions yet'} />
      </div>
      <Card className="sk-card--plain">
        <CardContent>
          {/* Pending / All segmented toggle — styled by .sk-tab-row */}
          <div className="sk-tab-row" style={{ marginBottom: 16 }}>
            <Button
              variant={tab === 'pending' ? 'contained' : 'outlined'}
              color="primary"
              onClick={() => setTab('pending')}
            >
              Pending
            </Button>
            <Button
              variant={tab === 'all' ? 'contained' : 'outlined'}
              color="primary"
              onClick={() => setTab('all')}
            >
              All
            </Button>

          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {(tab === 'pending'
              ? transactions.filter(tx => tx.status === 'pending')
              : transactions
            ).map((tx, i) => (
              <Surface key={i} className="sk-financial-card">
                {/* Header band: identity + status on the left, kind + amount on the right */}
                <header style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, padding: '4px 4px 14px', borderBottom: `1px solid var(--sk-border-soft)` }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <strong style={{ fontSize: 15, color: tokens.ink }}>{tx.userName}</strong>
                      <span style={{ fontSize: 13, color: tokens.muted }}>{tx.phone}</span>
                      <StatusPill status={tx.status} />
                    </div>
                    <div style={{ fontSize: 12, color: tokens.muted, marginTop: 4 }}>{new Date(tx.createdAt).toLocaleString()}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.065em', textTransform: 'uppercase', color: tokens.muted }}>{kindLabel(tx)}</div>
                    <strong style={{ fontSize: 22, fontWeight: 800, color: tokens.ink, letterSpacing: '-.02em' }}>{tx.amount} {tx.currency}</strong>
                  </div>
                </header>

                {/* Body: method / destination / refs + fee or breakdown detail items */}
                <DetailGrid>
                  <DetailItem label="Method" value={capitalise(tx.method)} />
                  {tx.details && <DetailItem label={destinationLabel(tx.method)} value={tx.details} />}
                  <DetailItem label="Ref">
                    <code style={{ fontSize: 11, fontFamily: 'monospace' }}>{tx._id}</code>
                  </DetailItem>
                  <DetailItem label="External ref" value={externalRefDisplay(tx)} />
                  {isBookingFeeRow(tx) && (
                    <DetailItem label="Skilz fee">
                      {actualFee(tx).toFixed(2)} {tx.currency}
                      {tx.feeSnapshot && (
                        <span style={{ color: tokens.muted }}>
                          {' '}({feeModeLabel(tx.feeSnapshot.mode)}
                          {bracketDescriptor(tx.feeSnapshot) ? ` · ${bracketDescriptor(tx.feeSnapshot)}` : ''})
                        </span>
                      )}
                    </DetailItem>
                  )}
                  {tx.feeSnapshot && tx.feeSnapshot.tipMinor > 0 && (
                    <DetailItem label="Tip">
                      Includes tip: {(tx.feeSnapshot.tipMinor / 100).toFixed(2)} {tx.currency}
                    </DetailItem>
                  )}
                  {isWithdrawal(tx) && withdrawalBreakdownItems(tx)}
                </DetailGrid>

                {isWithdrawal(tx) && withdrawalNetLine(tx)}

                {/* Actions / inline form for pending debits */}
                {!loading && (editing !== false && editing.transactionId === tx._id) ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 14 }}>
                    <input
                      type="text"
                      value={editing.externalRef || ''}
                      onChange={(e) => setEditing({ ...editing, externalRef: e.target.value, error: '' })}
                      placeholder={editing.status === 'confirmed' ? `External reference (transfer of $${netToTransfer(tx).toFixed(2)})` : 'Rejection reason'}
                      style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--sk-border)', fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' }}
                    />
                    {editing.error && <span style={{ fontSize: 12, color: tokens.danger }}>{editing.error}</span>}
                    <div style={{ display: 'flex', gap: 10 }}>
                      <ActionButton
                        variant="primary"
                        onClick={() => {
                          if (editing.externalRef === '') {
                            setEditing({ ...editing, error: 'Please enter a value for the external reference or rejection reason.' });
                            return;
                          }
                          const payload = { userId: editing.userId, transactionId: editing.transactionId, status: editing.status, externalRef: editing.externalRef };
                          httpClient(`https://skilzapp.net/api/users/updateTransaction`, {
                            method: 'PATCH',
                            body: JSON.stringify(payload)
                          })
                            .then(() => {
                              setEditing(false);
                              load();
                            })
                            .catch((err) => {
                              console.error(err);
                              setEditing(false);
                              load();
                            });
                        }}
                      >
                        Confirm
                      </ActionButton>
                      <ActionButton variant="ghost" onClick={() => { setEditing(false); }}>Cancel</ActionButton>
                    </div>
                  </div>
                ) : (tx.status === 'pending' && tx.type === 'debit') ? (
                  <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                    <ActionButton
                      variant="primary"
                      onClick={() => { setEditing({ userId: tx.userId, transactionId: tx._id, status: 'confirmed', externalRef: '', error: '' }); }}
                    >
                      Mark as paid
                    </ActionButton>
                    <ActionButton
                      variant="danger"
                      onClick={() => { setEditing({ userId: tx.userId, transactionId: tx._id, status: 'rejected', externalRef: '', error: '' }); }}
                    >
                      Reject
                    </ActionButton>
                  </div>
                ) : null}
              </Surface>
            ))}
          </div>
          {loading && <p>Loading...</p>}
        </CardContent>
      </Card>
    </main>
  );
}
